import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { Link2, Trash2, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { TRAINING_VIDEO_MAX_BYTES, type TrainingTopic, type TrainingTopicPayload } from "@xop/dashboard-shared";

import { ConfirmDialog } from "../../../components/ui/confirm-dialog.js";
import { trainingErrorMessage } from "./api.js";
import { TopicVideoPreview } from "./TopicVideoPreview.js";
import { durationInput, formatBytes, parseDuration, previewForLink } from "./trainingLogic.js";

export interface TopicPanelActions {
  onSave: (payload: TrainingTopicPayload) => Promise<void>;
  onUpload: (file: File) => Promise<void>;
  onRemoveVideo: () => Promise<void>;
}

/**
 * The selected topic: its words (title, summary, duration) and its ONE video.
 *
 * A video is either a LINK (YouTube, Vimeo, a direct .mp4 — pasted) or an
 * uploaded FILE (≤ 50 MB). Setting one replaces the other upstream: saving a
 * link purges an uploaded file, uploading replaces a link — so the panel
 * shows one current video and a "Replace" area underneath, never both.
 * Anything over 50 MB is refused here before a byte is sent, with the way
 * round it in the same sentence: host it and paste the link.
 *
 * The page keys this component on the topic id, so switching topics drops
 * unsaved text rather than writing it to the wrong topic.
 */
export function TopicPanel({
  topic,
  isSection,
  canEdit,
  actions,
}: {
  topic: TrainingTopic;
  isSection: boolean;
  canEdit: boolean;
  actions: TopicPanelActions;
}): JSX.Element {
  const [title, setTitle] = useState(topic.title);
  const [summary, setSummary] = useState(topic.summary ?? "");
  const [duration, setDuration] = useState(durationInput(topic.duration_seconds));
  const [saving, setSaving] = useState(false);

  const [source, setSource] = useState<"link" | "upload">(topic.video?.kind === "file" ? "upload" : "link");
  const [link, setLink] = useState("");
  const [videoBusy, setVideoBusy] = useState<null | "link" | "upload">(null);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const parsedDuration = parseDuration(duration);
  const durationInvalid = parsedDuration === undefined;
  const dirty =
    title.trim() !== topic.title ||
    (summary.trim() || null) !== (topic.summary ?? null) ||
    (!durationInvalid && parsedDuration !== (topic.duration_seconds ?? null));

  const saveText = async (): Promise<void> => {
    if (!title.trim() || durationInvalid) return;
    setSaving(true);
    try {
      await actions.onSave({
        title: title.trim(),
        summary: summary.trim() || null,
        duration_seconds: parsedDuration ?? null,
      });
    } catch {
      // reported by the page
    } finally {
      setSaving(false);
    }
  };

  const linkPreview = link.trim() ? previewForLink(link) : null;

  const saveLink = async (): Promise<void> => {
    if (!linkPreview || linkPreview.type === "invalid") return;
    setVideoBusy("link");
    setVideoError(null);
    try {
      await actions.onSave({ video_url: link.trim() });
      setLink("");
    } catch (err) {
      setVideoError(trainingErrorMessage(err));
    } finally {
      setVideoBusy(null);
    }
  };

  const upload = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    setVideoError(null);
    if (!file.type.startsWith("video/")) {
      setVideoError("That file isn't a video. Upload an .mp4, .webm or .mov file.");
      return;
    }
    if (file.size > TRAINING_VIDEO_MAX_BYTES) {
      setVideoError(
        `${file.name} is ${formatBytes(file.size)} — uploads stop at 50 MB. Put a larger video on YouTube, Vimeo or a file host and paste its link instead.`,
      );
      return;
    }
    setVideoBusy("upload");
    try {
      await actions.onUpload(file);
    } catch (err) {
      setVideoError(trainingErrorMessage(err));
    } finally {
      setVideoBusy(null);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  return (
    <Card variant="outlined" data-testid="topic-panel" data-topic-id={topic.id}>
      <CardContent sx={{ display: "grid", gap: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Chip size="small" label={isSection ? "Section" : "Lesson"} variant="outlined" />
          <Typography variant="h6" component="h2" sx={{ fontSize: 16, fontWeight: 600, flex: 1, minWidth: 0 }} noWrap>
            {topic.title}
          </Typography>
        </Box>

        {/* ── Video ───────────────────────────────────────────────── */}
        <Box sx={{ display: "grid", gap: 1.5 }}>
          {topic.video ? (
            <>
              <TopicVideoPreview video={topic.video} title={topic.title} />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 0, wordBreak: "break-all" }} data-testid="topic-current-video">
                  {topic.video.kind === "file"
                    ? `Uploaded: ${topic.video.filename} · ${formatBytes(topic.video.byte_size)}`
                    : `Link: ${topic.video.url}`}
                </Typography>
                {canEdit && (
                  <Button
                    size="small"
                    color="error"
                    startIcon={<Trash2 size={14} />}
                    onClick={() => setConfirmRemove(true)}
                    data-testid="topic-remove-video"
                  >
                    Remove video
                  </Button>
                )}
              </Box>
            </>
          ) : (
            <Box
              sx={{
                aspectRatio: "16 / 9",
                borderRadius: 1,
                border: 1,
                borderStyle: "dashed",
                borderColor: "divider",
                display: "grid",
                placeItems: "center",
                color: "text.secondary",
                bgcolor: "action.hover",
              }}
              data-testid="topic-no-video"
            >
              <Typography variant="body2">No video yet{canEdit ? " — add one below." : "."}</Typography>
            </Box>
          )}

          {canEdit && (
            <Box sx={{ display: "grid", gap: 1.25, p: 1.5, borderRadius: 1, border: 1, borderColor: "divider" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {topic.video ? "Replace the video" : "Add a video"}
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={source}
                  onChange={(_, v: "link" | "upload" | null) => {
                    if (v) {
                      setSource(v);
                      setVideoError(null);
                    }
                  }}
                >
                  <ToggleButton value="link" data-testid="video-source-link">
                    <Link2 size={14} style={{ marginRight: 6 }} /> Link
                  </ToggleButton>
                  <ToggleButton value="upload" data-testid="video-source-upload">
                    <Upload size={14} style={{ marginRight: 6 }} /> Upload
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {source === "link" ? (
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="https://www.youtube.com/watch?v=…"
                    value={link}
                    onChange={(e) => setLink(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") void saveLink();
                    }}
                    error={linkPreview?.type === "invalid"}
                    helperText={
                      linkPreview?.type === "invalid"
                        ? "Paste a full web address, starting https://"
                        : "YouTube, Vimeo, or a direct link to an .mp4 file."
                    }
                    inputProps={{ "data-testid": "topic-video-link-input", "aria-label": "Video link" }}
                  />
                  <Button
                    variant="contained"
                    disabled={!linkPreview || linkPreview.type === "invalid" || videoBusy !== null}
                    onClick={() => void saveLink()}
                    data-testid="topic-video-link-save"
                    sx={{ flexShrink: 0 }}
                  >
                    {videoBusy === "link" ? "Saving…" : "Use link"}
                  </Button>
                </Box>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<Upload size={16} />}
                    disabled={videoBusy !== null}
                  >
                    {videoBusy === "upload" ? "Uploading…" : "Choose a video file"}
                    <input
                      ref={fileInput}
                      hidden
                      type="file"
                      accept="video/*"
                      data-testid="topic-video-file-input"
                      onChange={(e) => void upload(e.target.files?.[0])}
                    />
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    Up to 50 MB. Larger videos go in as a link.
                  </Typography>
                </Box>
              )}

              {videoError && (
                <Alert severity="error" data-testid="topic-video-error">
                  {videoError}
                </Alert>
              )}
            </Box>
          )}
        </Box>

        <Divider />

        {/* ── Words ───────────────────────────────────────────────── */}
        <Stack spacing={2}>
          <TextField
            label="Title"
            size="small"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={!canEdit}
            required
            inputProps={{ "data-testid": "topic-title-input" }}
          />
          <TextField
            label="Summary"
            size="small"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            disabled={!canEdit}
            multiline
            minRows={2}
            placeholder="One or two sentences shown under the video"
            inputProps={{ "data-testid": "topic-summary-input" }}
          />
          <TextField
            label="Length"
            size="small"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            disabled={!canEdit}
            placeholder="m:ss"
            error={durationInvalid}
            helperText={durationInvalid ? "Type minutes and seconds, like 2:45" : "How long the video runs, e.g. 2:45"}
            sx={{ maxWidth: 220 }}
            inputProps={{ "data-testid": "topic-duration-input", inputMode: "numeric" }}
          />
          {canEdit && (
            <Box>
              <Button
                variant="contained"
                disabled={!dirty || saving || !title.trim() || durationInvalid}
                onClick={() => void saveText()}
                data-testid="topic-save"
              >
                {saving ? "Saving…" : "Save topic"}
              </Button>
            </Box>
          )}
        </Stack>
      </CardContent>

      <ConfirmDialog
        isOpen={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        title="Remove this video?"
        description={
          topic.video?.kind === "file"
            ? `The uploaded file ${topic.video.filename} is deleted. Resellers will skip this topic until it has a video again.`
            : "The link is cleared. Resellers will skip this topic until it has a video again."
        }
        confirmLabel="Remove video"
        onConfirm={async () => {
          await actions.onRemoveVideo();
        }}
      />
    </Card>
  );
}
